import { useEffect, useMemo, useState } from 'react';

/* Types */
import { TipShop } from '../TipShop';
import { PRICE_TYPES } from '../../../TipPublish/interfaces/utils';
import {
  FreeTipProduct,
  PaidTipProduct,
  PremiumTipProduct,
  TipProduct,
  Tipster,
} from '../interfaces/TipProduct';

/* Redux */
import {
  TipInfoFromBE,
  TipsInfoState,
  UnlockedTipData,
} from '../../../../store/tips/reducer';
import { LoginState } from '../../../../store/login/reducer';

interface useTipProductProps {
  getTipInfo: (tipID: string) => void;
  tipsInfoState: TipsInfoState;
  loginState: LoginState;
  tipID: string;
  getCoupons: () => void;
}

export const useTipProduct = ({
  getTipInfo,
  tipsInfoState,
  loginState,
  tipID,
  getCoupons,
}: useTipProductProps) => {
  const [shop, setShop] = useState<TipShop | null>(null);
  const [isUnavailable, setIsUnavailable] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const user = useMemo(() => loginState.user?.user, [loginState]);
  const areStatesLoading = useMemo(() => {
    const couponsStateDoneLoading = !!loginState.user?.user
      ? tipsInfoState.couponsRequested && !tipsInfoState.isCouponsLoading
      : true;
    const isDoneLoading =
      tipsInfoState.requested &&
      !tipsInfoState.isLoading &&
      (!!tipsInfoState.tipInfo || !!tipsInfoState.error) &&
      loginState.requested &&
      !loginState.isLoading &&
      couponsStateDoneLoading;

    return !isDoneLoading;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipsInfoState, loginState]);

  useEffect(() => {
    if (user) {
      getCoupons();
    }
    getTipInfo(tipID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const formatTipInfo = ({
    tip,
    tipster,
    matches,
  }: TipInfoFromBE): TipProduct => {
    const tipOwner: Tipster = new Tipster(
      tipster.id,
      tipster.name,
      tipster.avatar_url,
      tipster.share_channel_url,
      tipster.share_premium_url,
    );

    switch (tip.price_type) {
      case PRICE_TYPES.FREE:
        return new FreeTipProduct(tip as UnlockedTipData, tipOwner, matches);
      case PRICE_TYPES.PREMIUM:
        return new PremiumTipProduct(tip, tipOwner, matches);
      case PRICE_TYPES.PAID:
        return new PaidTipProduct(tip, tipOwner, matches);
      default:
        throw new Error('Invalid tip type');
    }
  };

  useEffect(() => {
    if (!areStatesLoading) {
      try {
        const { tipInfo } = tipsInfoState;
        if (tipInfo) {
          const { tipster: tipsterInfo } = tipInfo;
          const formattedTip = formatTipInfo(tipInfo);
          setShop(new TipShop([formattedTip], tipsterInfo));
        } else throw Error;
      } catch (e: any) {
        setIsUnavailable(true);
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areStatesLoading, tipsInfoState.coupons]);

  useEffect(() => {
    shop && setIsLoading(false);
    setReloadCount(reloadCount + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop]);

  return {
    isLoading,
    shop,
    isUnavailable,
  };
};
